import { HHStack, HHTypography } from '@hinge-health/react-component-library';
import { Link, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  buildProviderDetailPath,
  PROVIDER_HEALTH_LABELS,
} from '../constants/partner-data-gateway.constant';
import { useBasePath } from '../context/base-path-context';
import { ProviderSummary } from '../domain/types';
import { formatRelativeTime } from '../utils/format.utils';
import { getProviderHealth } from '../utils/provider-health.utils';
import { getStatusStyle, getToneColors } from '../utils/status-style.utils';
import { StatusChip } from './status-chip';
import { SurfaceCard } from './surface-card';

export const attentionCardTestId = (dataProvider: string): string =>
  `pdg-attention-card-${dataProvider}`;
export const ATTENTION_FAILING_TEXT = 'Latest file failed.';
export const ATTENTION_UNCONFIGURED_TEXT =
  'No configuration matches this bucket, so its files are rejected at the gate.';

const RULE_WIDTH_PX = 3;

interface AttentionCardProps {
  summary: ProviderSummary;
}

/** Why a provider is in "Needs a look", with the shortest path to act on it. */
export const AttentionCard = ({ summary }: AttentionCardProps): JSX.Element => {
  const basePath = useBasePath();
  const theme = useTheme();
  const health = getProviderHealth(summary);
  const { tone } = getStatusStyle(health);
  const why =
    health === 'failing' ? ATTENTION_FAILING_TEXT : ATTENTION_UNCONFIGURED_TEXT;
  const latest = summary.latestIngestion;

  return (
    <SurfaceCard
      data-testid={attentionCardTestId(summary.dataProvider)}
      paddingX={5}
      paddingY={4}
      sx={{
        borderLeft: `${RULE_WIDTH_PX}px solid ${getToneColors(theme, tone).fg}`,
      }}
    >
      <HHStack gap={2}>
        <HHStack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
        >
          <HHTypography
            hhVariant="variant-bypass"
            variant="body1"
            sx={{ fontWeight: 600, wordBreak: 'break-all' }}
          >
            {summary.dataProvider}
          </HHTypography>
          <StatusChip status={health} label={PROVIDER_HEALTH_LABELS[health]} />
        </HHStack>
        <HHTypography hhVariant="variant-bypass" variant="body2">
          {why}
        </HHTypography>
        {latest && (
          <HHTypography
            hhVariant="variant-bypass"
            variant="body2"
            color="text.secondary"
          >
            {`${latest.fileName} · ${formatRelativeTime(latest.createdAt)}`}
          </HHTypography>
        )}
        <Link
          component={RouterLink}
          to={`${basePath}/${buildProviderDetailPath(summary.dataProvider)}`}
          underline="hover"
          variant="body2"
          sx={{ fontWeight: 500, alignSelf: 'flex-start' }}
        >
          Open provider
        </Link>
      </HHStack>
    </SurfaceCard>
  );
};
